import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  VizCard,
  NowYouTry,
  DARK_INK,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// GoldFoilExperiment — Rutherford's 1909 gold-leaf scattering experiment, brought
// to life. A source on the LEFT fires a beam of alpha particles at a thin GOLD
// FOIL (a vertical column of gold atoms, each a big diffuse electron cloud with a
// TINY dense positive nucleus at its centre). The particles stream rightward and
// reveal the three fates Rutherford recorded on his zinc-sulfide screen:
//   • MOST sail straight through        — the atom is mostly empty space,
//   • SOME are deflected at an angle     — they passed near a nucleus,
//   • A FEW recoil almost straight BACK  — a near-head-on hit on the dense nucleus.
// Those three observations forced the conclusion drawn beneath the stage: a tiny,
// dense, positively-charged NUCLEUS carries almost all the mass, the atom is
// overwhelmingly empty space, and the electrons sit far out.
//
// config: { title?, foilAtoms? }   foilAtoms = how many gold atoms in the foil column.
//
// Cue (narration sync):
//   cue.play      run the firing animation (the beam streams + scatters)
//   cue.progress  0..1 scrubs one firing sweep (particles march L→R by progress)
//   cue.step      stage the story: 1 = fire / pass-through · 2 = deflection ·
//                 3 = back-scatter (each step layers in the next fate)
//   cue.highlight 'nucleus' | 'empty' | 'electrons' | a fate
//                 ('through'|'deflect'|'back') to spotlight that idea.
//
// At REST (no cue, reduced motion, or before play) a representative frozen frame
// is shown — a straight-through track, a deflected track, and a back-scattered
// track — so the whole idea reads without anything moving. Self-demo loops the
// firing when not cue-driven. SVG only; resilient to bad config; never throws.

export interface GoldFoilExperimentConfig {
  title?: string;
  /** Number of gold atoms stacked in the foil column (clamped 2..7). */
  foilAtoms?: number;
}

interface Props {
  config?: GoldFoilExperimentConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// ── Geometry ────────────────────────────────────────────────────────────────
const W = 380;
const H = 230;
const SOURCE_X = 30; // alpha source nozzle
const FOIL_X = 232; // x of the gold-atom column (nuclei sit on this line)
const ATOM_R = 26; // electron-cloud radius of a gold atom
const NUC_R = 3.1; // nucleus radius (deliberately tiny vs the cloud)
const ALPHA_R = 3.4; // drawn alpha-particle radius
const GOLD = '#D4A23A'; // warm gold token (nucleus + foil accents)
const GOLD_SOFT = 'rgba(212,162,58,0.16)'; // diffuse electron-cloud fill

type Fate = 'through' | 'deflect' | 'back';

// A single alpha particle: a launch lane (y at the source) plus a precomputed
// fate and the y at which it crosses the foil line. `phase` (0..1) is how far it
// has progressed along its own trajectory.
interface Alpha {
  id: number;
  laneY: number; // y at the source
  fate: Fate;
  impactY: number; // y where it reaches the foil line
  nucleusY: number; // y of the nucleus it interacts with (for deflect/back)
  deflectDir: number; // +1 down / -1 up, for the deflected exit angle
  speed: number; // phase units per second (slight variation for life)
  phase: number; // 0 = at source … 1 = fully resolved (off stage)
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

// Build the gold-atom column: evenly spaced down the foil line.
function buildFoil(count: number): number[] {
  const n = clamp(Math.round(Number.isFinite(count) ? count : 4), 2, 7);
  const top = 34;
  const bottom = H - 34;
  const ys: number[] = [];
  for (let i = 0; i < n; i++) {
    ys.push(top + ((bottom - top) * (i + 0.5)) / n);
  }
  return ys;
}

// Decide an alpha's fate from how close its straight-line lane passes to the
// nearest nucleus. A near-direct hit (tiny impact parameter) bounces BACK; a
// glancing pass DEFLECTS; everything else (the vast majority) goes THROUGH.
function makeAlpha(id: number, laneY: number, nuclei: number[]): Alpha {
  // Nearest nucleus to this lane.
  let nucleusY = nuclei[0] ?? H / 2;
  let best = Infinity;
  for (const ny of nuclei) {
    const d = Math.abs(ny - laneY);
    if (d < best) {
      best = d;
      nucleusY = ny;
    }
  }
  let fate: Fate;
  if (best < NUC_R + 2.2) fate = 'back'; // essentially head-on
  else if (best < ATOM_R * 0.5) fate = 'deflect'; // passed near the nucleus
  else fate = 'through'; // sailed through empty space
  const impactY = laneY;
  const deflectDir = laneY <= nucleusY ? -1 : 1; // pushed away from the nucleus
  return {
    id,
    laneY,
    fate,
    impactY,
    nucleusY,
    deflectDir,
    speed: 0.55 + (id % 5) * 0.045,
    phase: 0,
  };
}

// Position + drawn path of an alpha at a given phase. Returns the head point and
// the polyline of where it has been (a comet trail), plus an opacity for fade-out.
function alphaPath(a: Alpha): { x: number; y: number; trail: Array<[number, number]>; opacity: number } {
  // Distance the head has travelled, in x, mapped from phase. The full journey
  // spans source → foil → exit; phase 0..0.62 is the inbound leg to the foil.
  const inbound = 0.62;
  const trail: Array<[number, number]> = [[SOURCE_X, a.laneY]];

  if (a.phase <= inbound) {
    // Travelling toward the foil along the straight lane.
    const k = a.phase / inbound;
    const x = SOURCE_X + (FOIL_X - SOURCE_X) * k;
    trail.push([x, a.laneY]);
    return { x, y: a.laneY, trail, opacity: 1 };
  }

  // After the foil line — fate decides the outbound path.
  const k = (a.phase - inbound) / (1 - inbound); // 0..1 outbound progress
  trail.push([FOIL_X, a.laneY]);

  if (a.fate === 'through') {
    const x = FOIL_X + (W + 30 - FOIL_X) * k;
    trail.push([x, a.laneY]);
    return { x, y: a.laneY, trail, opacity: 1 - Math.max(0, k - 0.85) / 0.15 };
  }

  if (a.fate === 'deflect') {
    // Bends away from the nucleus at a moderate angle as it exits right.
    const angle = (24 + (a.id % 3) * 8) * a.deflectDir; // degrees
    const rad = (angle * Math.PI) / 180;
    const dist = (W + 30 - FOIL_X) * k;
    const x = FOIL_X + Math.cos(rad) * dist;
    const y = a.laneY + Math.sin(rad) * dist;
    trail.push([x, y]);
    return { x, y, trail, opacity: 1 - Math.max(0, k - 0.85) / 0.15 };
  }

  // back-scatter: recoils up-and-back toward the source side.
  const angle = (150 + (a.id % 3) * 12) * (a.deflectDir || 1); // ~back, splayed
  const rad = (angle * Math.PI) / 180;
  const dist = (FOIL_X - SOURCE_X + 20) * k;
  const x = FOIL_X + Math.cos(rad) * dist;
  const y = a.laneY + Math.sin(rad) * dist;
  trail.push([x, y]);
  return { x, y, trail, opacity: 1 - Math.max(0, k - 0.8) / 0.2 };
}

// A representative frozen frame: one of each fate, drawn at a readable phase so
// the three outcomes are all visible at rest.
const REST_PHASES: Record<Fate, number> = { through: 0.9, deflect: 0.82, back: 0.74 };

const FATE_INFO: Record<Fate, { label: string; color: string; note: string }> = {
  through: { label: 'Pass straight through', color: 'var(--ws-ink-2)', note: 'atom is mostly empty space' },
  deflect: { label: 'Deflected at an angle', color: GOLD, note: 'passed near the +nucleus' },
  back: { label: 'Bounced almost back', color: '#E0564B', note: 'near head-on hit on the dense nucleus' },
};

export default function GoldFoilExperiment({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);

  const nuclei = useMemo(() => buildFoil(config.foilAtoms ?? 4), [config.foilAtoms]);

  // Build a deterministic, well-mixed beam: lanes spread across the foil height
  // so the column reliably yields the full mix of through / deflect / back. We
  // bias a couple of lanes onto a nucleus (for the rare back-scatter) and a few
  // just beside one (deflection); the rest land in empty space.
  const beam = useMemo(() => {
    const lanes: number[] = [];
    const top = 22;
    const bottom = H - 22;
    const N = 22;
    for (let i = 0; i < N; i++) lanes.push(top + ((bottom - top) * i) / (N - 1));
    // Guarantee one near-direct hit and one glancing pass exist for the story.
    if (nuclei.length) {
      lanes.push(nuclei[Math.floor(nuclei.length / 2)]); // head-on → back
      lanes.push((nuclei[0] ?? H / 2) + ATOM_R * 0.34); // glancing → deflect
    }
    return lanes.map((y, i) => makeAlpha(i, y, nuclei));
  }, [nuclei]);

  // The live particle list (cloned from `beam`, phases advanced each frame).
  const alphasRef = useRef<Alpha[]>([]);
  const [, forceTick] = useState(0);
  const interactedRef = useRef(false);
  const [showTry, setShowTry] = useState(false);

  // Playback control. `running` ⇒ the rAF loop advances phases and re-seeds
  // particles in a rolling stream. `scrub` (0..1, or null) freezes the whole
  // beam at a single progress value (cue.progress). `staged` limits which fates
  // are active (cue.step layering). null staged ⇒ all fates.
  const [running, setRunning] = useState(false);
  const scrubRef = useRef<number | null>(null);
  const stagedRef = useRef<Set<Fate> | null>(null);
  const [, setStagedVersion] = useState(0);

  // Spotlight from cue.highlight: which idea/fate to emphasise (others dim).
  const tokens = cueHighlights(cue);
  const spotFate: Fate | null = tokens.includes('back')
    ? 'back'
    : tokens.includes('deflect')
      ? 'deflect'
      : tokens.includes('through')
        ? 'through'
        : null;
  const spotIdea = tokens.find((t) => t === 'nucleus' || t === 'empty' || t === 'electrons') ?? null;

  const markInteracted = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'gold_foil', foilAtoms: nuclei.length });
    }
  };

  // (Re)seed the live beam — clone the template, stagger start phases so the
  // stream looks continuous rather than a single synchronized volley.
  const seedBeam = (staggered = true) => {
    alphasRef.current = beam.map((a, i) => ({
      ...a,
      phase: staggered ? -(i % 11) * 0.09 : 0,
    }));
  };

  const startFiring = () => {
    scrubRef.current = null;
    seedBeam(true);
    setShowTry(false);
    setRunning(true);
  };

  // ── Self-demo: loop the firing when nothing is driving us. Under reduced
  // motion or an active cue we render the static rest frame instead.
  useEffect(() => {
    if (reduced || cueOn) {
      setRunning(false);
      scrubRef.current = null;
      stagedRef.current = null;
      return;
    }
    const hold = window.setTimeout(() => startFiring(), 500);
    return () => window.clearTimeout(hold);
    // Arm once on mount (re-seed handled by deps on `beam`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduced, cueOn, beam]);

  // ── Cue handling: play / progress / step. Supersedes the self-demo.
  useEffect(() => {
    if (!cue) return;
    setShowTry(false);

    // step layers in fates: 1=through, 2=through+deflect, 3=all three.
    if (typeof cue.step === 'number') {
      const s = clamp(Math.round(cue.step), 1, 3);
      const set = new Set<Fate>(['through']);
      if (s >= 2) set.add('deflect');
      if (s >= 3) set.add('back');
      stagedRef.current = set;
      setStagedVersion((v) => v + 1);
    } else {
      stagedRef.current = null;
      setStagedVersion((v) => v + 1);
    }

    if (typeof cue.progress === 'number') {
      // Freeze the whole beam at one progress value — particles marched L→R.
      scrubRef.current = clamp(cue.progress, 0, 1);
      setRunning(false);
      forceTick((t) => t + 1);
      return;
    }

    if (cue.play && !reduced) {
      startFiring();
      return;
    }

    // A cue with only step/highlight and no play/progress: show rest frame but
    // honour staging/spotlight (handled in render).
    scrubRef.current = null;
    setRunning(false);
    forceTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue, reduced]);

  // ── Animation loop: advance phases; recycle particles that finish so the
  // beam keeps streaming. After a full cohort has fired once, offer "Now you try".
  const cyclesRef = useRef(0);
  useAnimationFrame((dt) => {
    if (!running) return;
    let allDone = true;
    for (const a of alphasRef.current) {
      a.phase += dt * a.speed;
      if (a.phase >= 1) {
        // Recycle into a fresh launch for a continuous stream (self-demo only).
        if (!cueOn) {
          a.phase = -Math.random() * 0.4;
        }
      }
      if (a.phase < 1) allDone = false;
    }
    if (allDone) {
      cyclesRef.current += 1;
      setRunning(false);
      if (!cueOn && !reduced) setShowTry(true);
    }
    forceTick((t) => t + 1);
  }, running);

  // Decide what to draw for each alpha right now.
  // - scrub mode: every alpha at scrub progress (clamped to its inbound if its
  //   fate is filtered out by staging).
  // - running: live phases.
  // - rest: a curated trio at their REST_PHASES.
  const staged = stagedRef.current;
  const fateActive = (f: Fate) => !staged || staged.has(f);

  type Draw = Alpha & { drawPhase: number };
  let drawn: Draw[];

  if (scrubRef.current != null) {
    const p = scrubRef.current;
    drawn = beam
      .filter((a) => fateActive(a.fate))
      .map((a) => ({ ...a, drawPhase: p }));
  } else if (running) {
    drawn = alphasRef.current
      .filter((a) => fateActive(a.fate) && a.phase > 0 && a.phase < 1)
      .map((a) => ({ ...a, drawPhase: a.phase }));
  } else {
    // Rest frame: one representative of each (active) fate.
    const pick = (f: Fate) => beam.find((a) => a.fate === f);
    const trio: Draw[] = [];
    (['through', 'deflect', 'back'] as Fate[]).forEach((f) => {
      if (!fateActive(f)) return;
      const a = pick(f);
      if (a) trio.push({ ...a, drawPhase: REST_PHASES[f] });
    });
    // If somehow a fate didn't materialise, still show a couple of through tracks.
    if (!trio.length) {
      const a = beam[0];
      if (a) trio.push({ ...a, drawPhase: 0.9 });
    }
    drawn = trio;
  }

  const dim = (f: Fate) => (spotFate && spotFate !== f ? 0.18 : 1);
  const ideaDim = (idea: 'nucleus' | 'empty' | 'electrons') =>
    spotIdea && spotIdea !== idea ? 0.3 : 1;

  const fired = running || scrubRef.current != null;
  const subtitle = fired
    ? 'Firing α-particles at the foil — watch the three fates'
    : "Most pass through · some deflect · a few bounce back";

  return (
    <VizCard
      eyebrow="Atomic structure · Rutherford 1909"
      title={config.title ?? 'The Gold-Leaf Experiment'}
      subtitle={subtitle}
      reducedMotion={reduced}
      hint="The rare bounce-backs revealed a tiny, dense, positive nucleus — and that the atom is mostly empty space."
    >
      <div className="relative w-full">
        <NowYouTry show={showTry && !reduced && !cueOn} label="Watch the bounce-backs" />

        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full rounded-md border border-ws-border bg-ws-surface-2"
          style={{ maxHeight: 300 }}
          role="img"
          aria-label="Rutherford gold-foil experiment: an alpha-particle source firing at a thin gold foil, with most particles passing through, some deflecting, and a few bouncing back."
        >
          <defs>
            <radialGradient id="gf-cloud" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(212,162,58,0.30)" />
              <stop offset="70%" stopColor={GOLD_SOFT} />
              <stop offset="100%" stopColor="rgba(212,162,58,0.02)" />
            </radialGradient>
            <radialGradient id="gf-nuc" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#F2C964" />
              <stop offset="100%" stopColor={GOLD} />
            </radialGradient>
          </defs>

          {/* ── The alpha source (a small emitter on the left) ─────────────── */}
          <g opacity={0.95}>
            <rect x={SOURCE_X - 22} y={H / 2 - 16} width={26} height={32} rx={4} fill="var(--ws-surface)" stroke="var(--ws-border)" />
            <rect x={SOURCE_X - 4} y={H / 2 - 5} width={10} height={10} rx={2} fill="var(--ws-ink-3)" />
            <text x={SOURCE_X - 9} y={H / 2 + 28} textAnchor="middle" fontSize="8" fontWeight="700" fill="var(--ws-ink-3)">
              α source
            </text>
            <text x={SOURCE_X - 9} y={H / 2 + 38} textAnchor="middle" fontSize="7" fill="var(--ws-ink-3)">
              (radioactive)
            </text>
          </g>

          {/* ── The gold foil: a column of gold atoms (diffuse cloud + tiny
              nucleus). The line behind them reads as the thin leaf. ───────── */}
          <line
            x1={FOIL_X}
            y1={20}
            x2={FOIL_X}
            y2={H - 20}
            stroke={GOLD}
            strokeWidth={1.5}
            opacity={0.35}
          />
          <text x={FOIL_X} y={14} textAnchor="middle" fontSize="8" fontWeight="700" fill={GOLD}>
            GOLD FOIL
          </text>

          {nuclei.map((ny, i) => (
            <g key={`atom-${i}`}>
              {/* electron cloud — large + diffuse (mostly empty space) */}
              <circle cx={FOIL_X} cy={ny} r={ATOM_R} fill="url(#gf-cloud)" opacity={ideaDim('empty')} />
              <circle
                cx={FOIL_X}
                cy={ny}
                r={ATOM_R}
                fill="none"
                stroke={GOLD}
                strokeWidth={0.75}
                strokeDasharray="2 3"
                opacity={0.45 * ideaDim('electrons')}
              />
              {/* a couple of far-out electrons to make the cloud literal */}
              {[0.62, 0.86].map((rf, k) => {
                const ang = (i * 1.7 + k * 2.3) % (Math.PI * 2);
                const ex = FOIL_X + Math.cos(ang) * ATOM_R * rf;
                const ey = ny + Math.sin(ang) * ATOM_R * rf;
                return (
                  <circle key={`e-${i}-${k}`} cx={ex} cy={ey} r={1.6} fill="var(--ws-ink-3)" opacity={0.7 * ideaDim('electrons')} />
                );
              })}
              {/* the tiny dense positive nucleus */}
              <circle cx={FOIL_X} cy={ny} r={NUC_R} fill="url(#gf-nuc)" stroke="rgba(0,0,0,0.3)" strokeWidth={0.5} opacity={ideaDim('nucleus')} />
              <text x={FOIL_X} y={ny + 1.6} textAnchor="middle" fontSize="4.5" fontWeight="800" fill={DARK_INK} opacity={ideaDim('nucleus')}>
                +
              </text>
            </g>
          ))}

          {/* ── The alpha particles + their tracks ────────────────────────── */}
          {drawn.map((a) => {
            const { x, y, trail, opacity } = alphaPath({ ...a, phase: a.drawPhase });
            const info = FATE_INFO[a.fate];
            const o = opacity * dim(a.fate);
            if (o <= 0.02) return null;
            const pts = trail.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ');
            return (
              <g key={`a-${a.id}`} opacity={o}>
                <polyline
                  points={pts}
                  fill="none"
                  stroke={info.color}
                  strokeWidth={a.fate === 'through' ? 1.2 : 1.6}
                  strokeLinecap="round"
                  opacity={0.55}
                  strokeDasharray={a.fate === 'through' ? '1 0' : '1 0'}
                />
                <circle cx={x} cy={y} r={ALPHA_R} fill={info.color} stroke="rgba(0,0,0,0.25)" strokeWidth={0.6} />
              </g>
            );
          })}

          {/* ── Fate legend (right edge) ──────────────────────────────────── */}
          <g transform={`translate(${W - 4}, 22)`} textAnchor="end">
            {(['through', 'deflect', 'back'] as Fate[]).map((f, i) => (
              <g key={`lg-${f}`} transform={`translate(0, ${i * 14})`} opacity={dim(f)}>
                <text x={-12} y={3} fontSize="8" fontWeight="700" fill={FATE_INFO[f].color}>
                  {FATE_INFO[f].label}
                </text>
                <circle cx={-4} cy={0} r={3} fill={FATE_INFO[f].color} />
              </g>
            ))}
          </g>
        </svg>

        {/* ── Conclusions Rutherford drew ───────────────────────────────────── */}
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Conclusion
            color={GOLD}
            heading="Tiny dense nucleus"
            body="The few bounce-backs mean a small, dense, positively-charged core holds almost all the mass."
            lit={!spotIdea || spotIdea === 'nucleus'}
          />
          <Conclusion
            color="var(--ws-ink-2)"
            heading="Mostly empty space"
            body="Because most α-particles pass straight through, the atom is overwhelmingly empty."
            lit={!spotIdea || spotIdea === 'empty'}
          />
          <Conclusion
            color="var(--ws-accent)"
            heading="Electrons far out"
            body="The light electrons orbit far from the nucleus, in the diffuse cloud the particles glide through."
            lit={!spotIdea || spotIdea === 'electrons'}
          />
        </div>
      </div>
    </VizCard>
  );
}

function Conclusion({
  color,
  heading,
  body,
  lit,
}: {
  color: string;
  heading: string;
  body: string;
  lit: boolean;
}) {
  return (
    <div
      className="rounded-md border border-ws-border bg-ws-surface px-2.5 py-2"
      style={{ opacity: lit ? 1 : 0.4, transition: 'opacity 200ms' }}
    >
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-[11px] font-semibold text-ws-ink-2">{heading}</span>
      </div>
      <p className="mt-1 text-[10px] leading-snug text-ws-ink-3">{body}</p>
    </div>
  );
}
